import React, { useEffect, useRef } from 'react';
import { ToastVariant } from '../hooks/useToast';

interface ToastProps {
    visible: boolean;
    message: string;
    variant?: ToastVariant;
    onDismiss?: () => void;
}

export function Toast({ visible, message, variant = 'success', onDismiss }: ToastProps): JSX.Element | null {
    const prevVisibleRef = useRef(false);

    useEffect(() => {
        prevVisibleRef.current = visible;
    }, [visible]);

    const wasVisible = prevVisibleRef.current;
    if (!visible && !wasVisible) return null;

    const isError = variant === 'error';

    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        bottom: '1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.6rem',
        padding: '0.6rem 1rem',
        background: 'var(--ps-foreground-0)',
        color: 'var(--ps-foreground-900)',
        border: `1px solid ${isError ? 'var(--ps-danger-300, #fca5a5)' : 'var(--ps-foreground-300)'}`,
        borderRadius: '0.4rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        fontSize: '0.875rem',
        fontFamily: 'inherit',
        width: '22rem',
        height: 'fit-content',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.6s ease',
        pointerEvents: isError ? 'auto' : 'none',
    };

    const iconStyle: React.CSSProperties = {
        flexShrink: 0,
        marginTop: '0.1rem',
        width: '1.1rem',
        height: '1.1rem',
        borderRadius: '50%',
        background: isError ? 'var(--ps-danger-500, #dc2626)' : 'var(--ps-success-200)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <div role={isError ? 'alert' : 'status'} aria-live={isError ? 'assertive' : 'polite'} style={containerStyle}>
            <span style={iconStyle} aria-hidden="true">
                {isError ? (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 2L8 8M8 2L2 8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                ) : (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </span>
            <span style={{ flex: 1 }}>{message}</span>
            {isError && onDismiss && (
                <button
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    style={{
                        flexShrink: 0,
                        marginTop: '-0.1rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 0 0 0.25rem',
                        color: 'var(--ps-foreground-500, #6b7280)',
                        fontSize: '1rem',
                        lineHeight: 1,
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
}
